/**
 * استایل و تشخیص تأخیر حضور و غیاب — مطابق ATTENDANCE_LATE_AND_COLOR_STYLING_FRONTEND_GUIDE
 */

/** تشخیص امن تأخیر از فیلدهای جدید و قدیمی */
export const resolveIsLate = (cell) => {
  if (!cell || typeof cell !== 'object') return false;
  if (cell.is_late === true) return true;
  if (cell.late === true) return true;
  if (cell.late === 'yes' || cell.late === 'Yes' || cell.late === 1 || cell.late === '1') {
    return true;
  }
  const status = String(cell.status || '').toLowerCase().replace(/_/g, '');
  return status === 'late';
};

export const normalizeAttendanceStatusKey = (status) => {
  if (status == null || status === '') return null;
  const raw = String(status).trim();
  const key = raw.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  const map = {
    present: 'present',
    absent: 'absent',
    leave: 'leave',
    half_day_leave: 'half_day_leave',
    halfday: 'half_day',
    half_day: 'half_day',
    holiday: 'holiday',
    day_off: 'day_off',
    dayoff: 'day_off',
    late: 'late',
    mission: 'mission',
  };
  return map[key] || map[raw] || key;
};

/**
 * پالت استاندارد UI از راهنما (رنگ‌های hex برای استایل inline آنت دیزاین)
 */
export const ATTENDANCE_STATUS_PALETTE = {
  late: {
    text: 'تأخیر',
    color: '#92400e', // amber-800
    bg: '#fef3c7', // amber-100
    border: '#fcd34d', // amber-300
    title: 'ورود با تأخیر',
  },
  present: {
    text: 'حاضر',
    color: '#047857', // emerald-700
    bg: '#ecfdf5', // emerald-50
    border: '#a7f3d0', // emerald-200
    title: 'حضور به‌موقع',
  },
  absent: {
    text: 'غیبت',
    color: '#be123c', // rose-700
    bg: '#ffe4e6', // rose-100
    border: '#fda4af', // rose-300
    title: 'غیبت',
  },
  leave: {
    text: 'مرخصی',
    color: '#1d4ed8', // blue-700
    bg: '#eff6ff', // blue-50
    border: '#bfdbfe', // blue-200
    title: 'مرخصی',
  },
  half_day_leave: {
    text: 'مرخصی ۱/۲',
    color: '#1d4ed8',
    bg: '#eff6ff',
    border: '#bfdbfe',
    title: 'مرخصی نیم‌روز',
  },
  half_day: {
    text: 'نیم‌روز',
    color: '#c2410c', // orange-700
    bg: '#fff7ed', // orange-50
    border: '#fed7aa', // orange-200
    title: 'نیمه‌وقت / تعجیل',
  },
  holiday: {
    text: 'تعطیل',
    color: '#64748b', // slate-500
    bg: '#f1f5f9', // slate-100
    border: '#e2e8f0', // slate-200
    title: 'تعطیل رسمی',
  },
  day_off: {
    text: 'استراحت',
    color: '#64748b',
    bg: '#f1f5f9',
    border: '#e2e8f0',
    title: 'تعطیل هفتگی',
  },
  mission: {
    text: 'مأموریت',
    color: '#3730a3', // indigo-800
    bg: '#eef2ff', // indigo-50
    border: '#a5b4fc', // indigo-300
    title: 'مأموریت کاری',
  },
};

/** کلاس‌های Tailwind مطابق جدول راهنما */
export const ATTENDANCE_STATUS_TW = {
  late: 'bg-amber-100 border-amber-300 text-amber-800 font-semibold dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-700',
  present:
    'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800',
  absent:
    'bg-rose-100 border-rose-300 text-rose-700 font-bold dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800',
  leave:
    'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800',
  half_day_leave:
    'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800',
  half_day:
    'bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300 dark:border-orange-800',
  holiday:
    'bg-slate-100 border-slate-200 text-slate-500 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-700',
  day_off:
    'bg-slate-100 border-slate-200 text-slate-500 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-700',
  mission:
    'bg-indigo-50 border-indigo-300 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-700',
};

/**
 * از سلول ماتریس/ساعتی، کلید بصری نهایی را برمی‌گرداند
 * اولویت: تأخیر روی present → half_day → status
 */
export const resolveAttendanceVisualKey = (cell) => {
  if (cell == null || cell === '') return null;
  if (typeof cell === 'string') {
    const s = cell.trim();
    if (/^holiday$/i.test(s)) return 'holiday';
    if (/^absent$/i.test(s)) return 'absent';
    if (/^day\s*off$/i.test(s) || /^day_off$/i.test(s)) return 'day_off';
    if (/^leave$/i.test(s)) return 'leave';
    if (/^mission$/i.test(s)) return 'mission';
    if (/^present$/i.test(s)) return 'present';
    if (/^late$/i.test(s)) return 'late';
    return normalizeAttendanceStatusKey(s);
  }

  // مأموریت تأییدشده — هرگز به‌عنوان غیبت دیده نشود
  if (cell.mission || cell.status === 'mission' || cell.mission_id != null) {
    return 'mission';
  }

  const statusKey = normalizeAttendanceStatusKey(cell.status);
  if (statusKey === 'mission') return 'mission';

  const halfDay = cell.half_day === true || cell.half_day === 'yes' || statusKey === 'half_day';
  const isLate = resolveIsLate(cell);

  if (isLate && (statusKey === 'present' || statusKey === 'late' || !statusKey)) {
    return 'late';
  }
  if (halfDay && statusKey !== 'leave' && statusKey !== 'half_day_leave') {
    return 'half_day';
  }
  if (statusKey === 'half_day_leave') return 'half_day_leave';
  return statusKey || (isLate ? 'late' : null);
};

export const getAttendancePalette = (visualKey) =>
  ATTENDANCE_STATUS_PALETTE[visualKey] || null;
